import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Configurar axios con token por defecto
  const setupAxiosInterceptors = (authToken) => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Verificar token al cargar la aplicación
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const savedToken = localStorage.getItem('saver-auth-token');
        if (savedToken) {
          setupAxiosInterceptors(savedToken);
          
          // Verificar si el token es válido
          const response = await axios.get('/api/verify');
          if (response.data.valid) {
            setToken(savedToken);
            setUser(response.data.user);
            setIsLoggedIn(true);
          } else {
            // Token inválido, limpiar
            localStorage.removeItem('saver-auth-token');
            setupAxiosInterceptors(null);
          }
        }
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        localStorage.removeItem('saver-auth-token');
        setupAxiosInterceptors(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Función de login
  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/login', {
        username,
        password
      });

      const { token: authToken, user: userData } = response.data;
      
      // Guardar token en localStorage
      localStorage.setItem('saver-auth-token', authToken);
      
      // Configurar axios
      setupAxiosInterceptors(authToken);
      
      // Actualizar estado
      setToken(authToken);
      setUser(userData);
      setIsLoggedIn(true);
      
      return { success: true, message: 'Login exitoso' };
    } catch (error) {
      console.error('Error en login:', error);
      const message = error.response?.data?.message || 'Error en el servidor';
      return { success: false, message };
    }
  };

  // Función de logout
  const logout = () => {
    localStorage.removeItem('saver-auth-token');
    setupAxiosInterceptors(null);
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
  };

  // Manejar expiración de token
  useEffect(() => {
    const handleTokenExpiry = (error) => {
      if (error.response?.status === 401) {
        logout();
      }
    };

    // Interceptor para manejar errores de autenticación
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        handleTokenExpiry(error);
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const value = {
    user,
    token,
    isLoggedIn,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 